import React, { useCallback, useEffect, useState } from 'react';
import { FmlxButton } from 'fmlx-common-ui';
import EICheckBox from 'components/customComponents/Layers/EICheckBox';
import ELayersView from 'enums/ELayersView';
import { ExcludeIngredientProps, TSelectIngredient } from 'business/models/CustomModels';
import SelectIngredients from 'components/customComponents/Layers/SelectIngredients';
import { GetIngredients } from 'utils/Global';
import {
  IngredientDTO,
  LayerDTO,
  RandomLayerDTO,
  RandomLayerExcludeListDTO,
} from 'business/models/Models';
import { usePreserveExperiment, useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  selectExperimentLayers,
  selectSelectedLayerId,
  selectSelectedLayer,
} from 'redux/selectors/ExperimentStateSelector';
import { updateExperimentLayers } from 'redux/reducers/experimentDataReducer';
import { cloneDeep } from 'lodash';
import { withTabBackButton } from 'components/tab';

function ExcludeIngredients({
  editExcludeGroupId,
  setEditExcludeGroupId,
  setContentView,
  handleBackToPreviousScreen,
  setHandleOnTabBackPress,
}: ExcludeIngredientProps) {
  const [excludeContentView, setExcludeContentView] = useState<string>(
    ELayersView.SELECTINGREDIENTSVIEW,
  );
  const [selectedIngNum, setSelectedIngNum] = useState<number>(0);
  const [firstIngredient, setFirstIngredient] = useState<TSelectIngredient>({
    name: 'Select Ingredient',
    id: 0,
  });
  const [secondIngredient, setSecondIngredient] = useState<TSelectIngredient>({
    name: 'Select Ingredient',
    id: 0,
  });

  const dispatch = useRMDispatch();
  const _layers = useRMSelector(selectExperimentLayers);
  const _selectedLayerId = useRMSelector(selectSelectedLayerId);
  const _selectedLayer = useRMSelector(selectSelectedLayer);
  const _setLayers = useCallback(
    (lyrs) => dispatch(updateExperimentLayers(lyrs)),
    [dispatch],
  );
  const callPreserveUndoableState = usePreserveExperiment();

  useEffect(() => {
    const lyr: RandomLayerDTO = [..._layers].find((l) => l.id === _selectedLayerId)
      .layerDetail as RandomLayerDTO;
    if (editExcludeGroupId !== 0) {
      const excludeGroup = lyr.randomLayerExcludeList.find(
        (e) => e.id === editExcludeGroupId,
      );
      setFirstIngredient({
        id: excludeGroup.ingredient1.id,
        name: excludeGroup.ingredient1.longName,
      });
      setSecondIngredient({
        id: excludeGroup.ingredient2.id,
        name: excludeGroup.ingredient2.longName,
      });
      setExcludeContentView(ELayersView.SELECTEXCLUDEINGREDIENT);
    }
  }, [editExcludeGroupId]);

  useEffect(() => {
    if (excludeContentView === ELayersView.SELECTINGREDIENTSVIEW) {
      setHandleOnTabBackPress(() =>
        handleBackToPreviousScreen(ELayersView.LAYERDESIGNVIEW),
      );
    } else {
      setHandleOnTabBackPress(() => handleBackToSelectIngredientsView());
    }
  }, [setHandleOnTabBackPress, excludeContentView]);

  const handleBackToSelectIngredientsView = useCallback(
    () => () => {
      setExcludeContentView(ELayersView.SELECTINGREDIENTSVIEW);
    },
    [],
  );

  function addExcludeGroup(ings: IngredientDTO[], layer: LayerDTO) {
    const lyr: RandomLayerDTO = layer.layerDetail as RandomLayerDTO;
    const newGroup: RandomLayerExcludeListDTO = new RandomLayerExcludeListDTO();
    newGroup.ingredient1 = new IngredientDTO({ ...ings[0] });
    newGroup.ingredient2 = new IngredientDTO({ ...ings[1] });
    newGroup.ingredient1Id = ings[0].id;
    newGroup.ingredient2Id = ings[1].id;
    newGroup.randomLayerId = layer.id;
    newGroup.id = -1 - lyr.randomLayerExcludeList.length;
    lyr.randomLayerExcludeList.push(newGroup);
    setEditExcludeGroupId(0);
    setContentView(ELayersView.LAYERDESIGNVIEW);
  }
  function editExcludeGroup(ings: IngredientDTO[], id, layer: LayerDTO) {
    const randomLayerExcludeGroup: RandomLayerExcludeListDTO = (
      layer.layerDetail as RandomLayerDTO
    ).randomLayerExcludeList.find((e) => e.id === id);
    randomLayerExcludeGroup.ingredient1 = new IngredientDTO({ ...ings[0] });
    randomLayerExcludeGroup.ingredient2 = new IngredientDTO({ ...ings[1] });
    randomLayerExcludeGroup.ingredient1Id = ings[0].id;
    randomLayerExcludeGroup.ingredient2Id = ings[1].id;
    setEditExcludeGroupId(0);
    setContentView(ELayersView.LAYERDESIGNVIEW);
  }
  function onApplyClick() {
    const ing: IngredientDTO[] = GetIngredients().ingredients.filter(
      (ing) =>
        ing.longName === firstIngredient.name || ing.longName === secondIngredient.name,
    );
    const index = _layers.findIndex((l) => l.id == _selectedLayer.id);
    const tempLayers = cloneDeep(_layers);

    tempLayers[index].dirty = true;
    if (editExcludeGroupId === 0) {
      addExcludeGroup(ing, tempLayers[index]);
    } else {
      editExcludeGroup(ing, editExcludeGroupId, tempLayers[index]);
    }
    callPreserveUndoableState();
    _setLayers(tempLayers);
    setHandleOnTabBackPress(null);
  }

  function onClickSelectIngredient(ingNum) {
    setSelectedIngNum(ingNum);
    setExcludeContentView(ELayersView.SELECTINGREDIENTSVIEW);
  }
  if (excludeContentView === ELayersView.SELECTINGREDIENTSVIEW) {
    return selectedIngNum === 0 ? (
      <SelectIngredients id='commandpanel-randomlayer-excludeing-selectfirsting'
        setContentView={setExcludeContentView}
        setSelectIngredient={setFirstIngredient}
        setHandleOnTabBackPress={setHandleOnTabBackPress}
      />
    ) : (
      <SelectIngredients id='commandpanel-randomlayer-excludeing-selectseconding'
        setContentView={setExcludeContentView}
        setSelectIngredient={setSecondIngredient}
        setHandleOnTabBackPress={setHandleOnTabBackPress}
      />
    );
  }
  return (
    <div id='commandpanel-randomlayer-excludeing-wrapper' style={{ margin: '0 4.364%' }}>
      <EICheckBox
        labelText="Ingredient 1"
        textBoxText={firstIngredient.name}
        active={firstIngredient.name !== 'Select Ingredient'}
        onClick={() => onClickSelectIngredient(0)}
      />
      <EICheckBox
        labelText="Ingredient 2"
        textBoxText={secondIngredient.name}
        active={secondIngredient.name !== 'Select Ingredient'}
        onClick={() => onClickSelectIngredient(1)}
      />
      {firstIngredient.name === secondIngredient.name ? (
        <div id='commandpanel-randomlayer-excludeing-seconding-message'
          className="flex"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '5px',
            color: 'red',
          }}
        >
          Ing. 2 cannot be same as Ing. 1
        </div>
      ) : null}
      <div id="commandpanel-randomlayer-excludeing-applybutton-container"
        className="flex"
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '5px',
        }}
      >
        <FmlxButton id="commandpanel-randomlayer-excludeing-applybutton"
          style={{
            background: '#008F40',
            color: '#ffffff',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          disabled={
            firstIngredient.name === 'Select Ingredient' ||
            secondIngredient.name === 'Select Ingredient' ||
            firstIngredient.name === secondIngredient.name
          }
          type={FmlxButton.Type.POSITIVE}
          onClick={(e) => onApplyClick()}
          label="Apply"
        />
      </div>
    </div>
  );
}

export default withTabBackButton(ExcludeIngredients);
